import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FolderOutlinedIcon,
  ShieldOutlinedIcon,
  SearchRoundedIcon,
  CheckCircleOutlineRoundedIcon,
  Close,
} from "../../utilities/icons";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { editAccess, projectsApi } from "../../apiCalls/Apicalls";
import { extractProjectAccessLabels } from "../../utilities/projectAccess";

const statusPalette = {
  growing: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#22c55e",
  },
  active: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#22c55e",
  },
  "on hold": {
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    color: "#f59e0b",
  },
  completed: {
    backgroundColor: "#eef1f7",
    color: "#6b7280",
  },
};

const BLUE = "#418BFF";
const BLUE_OVERLAY = "rgba(65, 139, 255, 0.12)";

function initialsForUser(user) {
  return `${user?.FirstName || ""} ${user?.LastName || ""}`
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function AddAccess({
  open,
  onClose,
  selectedRow,
  selectedareas,
  setSelectedareas,
  userData,
  filteredOptions,
  setFilteredOptions,
  fetchUsers,
}) {
  const [projects, setProjects] = useState([]);
  const [accessData, setAccessData] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const activeUser = useMemo(
    () => selectedRow || userData || {},
    [selectedRow, userData],
  );
  const currentAccess = useMemo(() => activeUser?.Access || [], [activeUser]);
  const fullName =
    `${activeUser?.FirstName || ""} ${activeUser?.LastName || ""}`.trim() ||
    activeUser?.FullName ||
    "Selected User";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(projectsApi);
        const nextProjects = response.data || [];
        setProjects(nextProjects);
        setAccessData(extractProjectAccessLabels(nextProjects));
      } catch (error) {
        console.error("Error fetching access labels:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const nextFilteredOptions = accessData.filter(
      (option) => !currentAccess.includes(option),
    );
    setFilteredOptions(nextFilteredOptions);
  }, [accessData, currentAccess, setFilteredOptions]);

  useEffect(() => {
    setSelectedareas([]);
  }, [activeUser?._id, setSelectedareas]);

  const assignedProjects = useMemo(() => {
    const selectedLabels = new Set([
      ...currentAccess,
      ...(selectedareas || []),
    ]);
    const lookupName = fullName.toLowerCase();
    const lookupId = (activeUser?.EmpId || "").toLowerCase();

    return projects
      .map((project) => {
        const matchedLabels = (project?.accessCatalog || [])
          .map((entry) => entry?.label)
          .filter(Boolean)
          .filter((label) => selectedLabels.has(label));

        const members = project?.members || [];
        const isMember = members.some((member) => {
          const normalized = String(member).toLowerCase();
          return normalized === lookupName || normalized === lookupId;
        });

        if (!matchedLabels.length && !isMember) {
          return null;
        }

        return {
          id: project?.id || project?._id || project?.name,
          name: project?.name || "Untitled project",
          code: project?.code,
          health: project?.health || (isMember ? "Active" : "Mapped"),
          matchedLabels,
          isMember,
        };
      })
      .filter(Boolean);
  }, [activeUser?.EmpId, currentAccess, fullName, projects, selectedareas]);

  const hasAllAccessAssigned =
    accessData.length > 0 && currentAccess.length >= accessData.length;

  const handleCheckboxChange = (event, value) => {
    setSelectedareas(value);
  };

  const handleAddAreas = async () => {
    if (selectedareas?.length === 0) {
      toast.error("Please select at least one role.");
      return;
    }

    try {
      setSavingAccess(true);
      const empid = activeUser?._id;
      if (!empid) {
        toast.error("Unable to find the selected user.");
        return;
      }

      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      };

      await axios.put(
        `${editAccess}/${empid}`,
        { Access: selectedareas },
        config,
      );

      fetchUsers();
      toast.success("Access updated successfully");
      onClose();
      setSelectedareas([]);
    } catch (error) {
      toast.error("Error updating access areas");
    } finally {
      setSavingAccess(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedareas(filteredOptions || []);
  };

  const removeSelectedArea = (areaToRemove) => {
    setSelectedareas((previous) =>
      previous.filter((area) => area !== areaToRemove),
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 620 },
          maxHeight: "calc(100vh - 32px)",
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "var(--ax-shadow-lg)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={{ xs: 2, sm: 3 }}
          py={2.25}
          borderBottom="1px solid var(--ax-border)"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: BLUE_OVERLAY,
                color: BLUE,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "10px",
              }}
            >
              {initialsForUser(activeUser)}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.96rem",
                  color: "var(--ax-ink)",
                }}
              >
                {fullName}
              </Typography>
              <Typography
                sx={{ color: "var(--ax-ink-2)", fontSize: "0.76rem" }}
              >
                Employee ID: {activeUser?.EmpId || "N/A"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={savingAccess}
            size="small"
            sx={{ color: "var(--ax-ink-2)" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ overflowY: "auto" }}>
          <Box px={{ xs: 2, sm: 3 }} py={2.5}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: "var(--ax-ink-2)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
                mb: 1.25,
              }}
            >
              Current roles
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {currentAccess.length > 0 ? (
                currentAccess.map((role) => (
                  <Chip
                    key={role}
                    icon={
                      <ShieldOutlinedIcon
                        sx={{ fontSize: "14px !important" }}
                      />
                    }
                    label={role}
                    size="small"
                    sx={{
                      bgcolor: BLUE_OVERLAY,
                      color: BLUE,
                      borderRadius: "6px",
                      fontWeight: 500,
                      fontSize: "0.74rem",
                    }}
                  />
                ))
              ) : (
                <Typography
                  sx={{ color: "var(--ax-ink-3)", fontSize: "0.86rem" }}
                >
                  No roles currently assigned.
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "var(--ax-border)" }} />

          <Box px={{ xs: 2, sm: 3 }} py={2.5}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: "var(--ax-ink-2)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
                mb: 1.25,
              }}
            >
              Add / manage access
            </Typography>

            {hasAllAccessAssigned ? (
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{
                  minHeight: 140,
                  border: "1px dashed var(--ax-blue-soft)",
                  borderRadius: "var(--ax-radius-md)",
                  bgcolor: "var(--ax-blue-overlay)",
                }}
              >
                <CheckCircleOutlineRoundedIcon
                  sx={{ fontSize: 32, color: BLUE }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.92rem",
                    color: "var(--ax-ink)",
                  }}
                >
                  All access assigned
                </Typography>
                <Typography
                  sx={{ fontSize: "0.82rem", color: "var(--ax-ink-2)" }}
                >
                  This user already has every available role.
                </Typography>
              </Stack>
            ) : (
              <>
                <Autocomplete
                  multiple
                  options={filteredOptions || []}
                  value={selectedareas}
                  onChange={handleCheckboxChange}
                  disabled={savingAccess}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  renderTags={() => null}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        size="small"
                        checked={selected || currentAccess.includes(option)}
                        sx={{
                          mr: 1,
                          "&.Mui-checked": { color: BLUE },
                        }}
                      />
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        spacing={1}
                      >
                        <Typography
                          sx={{ fontSize: "0.84rem", color: "var(--ax-ink)" }}
                        >
                          {option}
                        </Typography>
                        {currentAccess.includes(option) ? (
                          <Chip
                            label="Current"
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: BLUE_OVERLAY,
                              color: BLUE,
                              fontSize: "0.68rem",
                              fontWeight: 500,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select roles to add..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Stack direction="row" alignItems="center">
                            <SearchRoundedIcon
                              sx={{
                                color: "var(--ax-ink-3)",
                                mt: 0.5,
                                fontSize: 18,
                              }}
                            />
                            {params.InputProps.startAdornment}
                          </Stack>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      alignItems: "flex-start",
                      borderRadius: "10px",
                      py: 0.4,
                    },
                  }}
                />

                <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
                  {selectedareas.length > 0 ? (
                    selectedareas.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        onDelete={
                          savingAccess
                            ? undefined
                            : () => removeSelectedArea(role)
                        }
                        sx={{
                          borderRadius: "6px",
                          bgcolor: "var(--ax-bg-secondary)",
                          color: "var(--ax-ink)",
                          fontSize: "0.74rem",
                          fontWeight: 500,
                        }}
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{ color: "var(--ax-ink-3)", fontSize: "0.82rem" }}
                    >
                      Choose one or more roles to add for this user.
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Box>

          <Divider sx={{ borderColor: "var(--ax-border)" }} />

          <Box px={{ xs: 2, sm: 3 }} py={2.5}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: "var(--ax-ink-2)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
                mb: 1.25,
              }}
            >
              Assigned projects
            </Typography>
            <Stack spacing={1}>
              {assignedProjects.length > 0 ? (
                assignedProjects.map((project) => {
                  const statusKey = String(
                    project.health || "completed",
                  ).toLowerCase();
                  const statusStyle = statusPalette[statusKey] || {
                    backgroundColor: BLUE_OVERLAY,
                    color: BLUE,
                  };

                  return (
                    <Stack
                      key={project.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        border: "1px solid var(--ax-border)",
                        borderRadius: "var(--ax-radius-md)",
                        px: 1.75,
                        py: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            display: "grid",
                            placeItems: "center",
                            bgcolor: BLUE_OVERLAY,
                            color: BLUE,
                          }}
                        >
                          <FolderOutlinedIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.88rem",
                              color: "var(--ax-ink)",
                            }}
                          >
                            {project.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: "var(--ax-ink-2)",
                              fontSize: "0.76rem",
                            }}
                          >
                            {project.code ? `${project.code} · ` : ""}
                            {project.matchedLabels.length > 0
                              ? project.matchedLabels.join(", ")
                              : "Project member"}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={project.health}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 500,
                          fontSize: "0.72rem",
                          borderRadius: "6px",
                          ...statusStyle,
                        }}
                      />
                    </Stack>
                  );
                })
              ) : (
                <Typography
                  sx={{ color: "var(--ax-ink-3)", fontSize: "0.86rem" }}
                >
                  No project mappings found for this user&apos;s current or
                  selected roles.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          px={{ xs: 2, sm: 3 }}
          py={2}
          borderTop="1px solid var(--ax-border)"
        >
          <Button
            onClick={handleSelectAll}
            disabled={!filteredOptions?.length || savingAccess}
            variant="text"
            size="small"
            sx={{ fontWeight: 500 }}
          >
            Select all roles
          </Button>
          <Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={savingAccess}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={selectedareas?.length === 0 || savingAccess}
              onClick={handleAddAreas}
            >
              {savingAccess ? "Saving..." : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}

export default AddAccess;
